import React from 'react'
import {
    BreakingNewsThemeName,
    StyledBreakingNews,
    StyledBreakingNewsTitle,
    StyledBreakingNewsMark,
    StyledAnnouncementMark,
    StyledIconChevronRight,
    StyledSevenCricketLogo,
    StyledWrapper,
    StyledLogoContainer,
    StyledTheGameLogo,
    BreakingNewsThemeNames,
    StyledCricketPlayerImage,
    StyledCricketPlayerWrapper,
    StyledPick11Logo,
} from 'component-library/content/BreakingNews/BreakingNews.styled'
import { ImpressionAvailable } from 'component-library/__helpers/impression-available-helper'
import {
    CollectionEvent,
    createCollectionAvailableEvent,
    createCardClickedEvent,
} from 'common/events/collection-events'
import { PublicationCardItem, BreakingNewsCard } from 'common/client-data-types/card-types'

export interface Props {
    titlePrefix?: string
    breakingNewsTheme: string | undefined
    item: PublicationCardItem
    cardNumber: number
    onEvent: (event: CollectionEvent) => void
}

function getTheme(theme: string | undefined): BreakingNewsThemeName {
    if (BreakingNewsThemeNames.indexOf(theme as BreakingNewsThemeName) > -1) {
        return theme as BreakingNewsThemeName
    }

    return 'default'
}

export const renderTitlePrefix = (
    titlePrefix: string | undefined,
    newsTheme: BreakingNewsThemeName | undefined
) => {
    if (!titlePrefix && newsTheme) {
        switch (newsTheme) {
            case 'announcement':
            case 'thegame':
                return ''
            case 'cricket':
                return 'Cricket: Live and Free on Seven'
            case 'csk':
                return 'Latest:'
            default:
                return 'Breaking News:'
        }
    }

    return titlePrefix || 'Breaking News:'
}

export const BreakingNews: React.FC<Props> = ({
    item,
    cardNumber,
    breakingNewsTheme,
    titlePrefix,
    onEvent,
}) => {
    if (!item.shortHeadline) {
        return null
    }

    const isAnnouncement = breakingNewsTheme === 'announcement'

    const newItem: BreakingNewsCard = {
        ...item,
        cardType: 'breaking-news',
    }

    const cardFormat = isAnnouncement ? 'Announcement' : 'BreakingNews'

    const linkClicked = () => {
        onEvent(createCardClickedEvent(newItem, 'BreakingNews', cardFormat, cardNumber))
    }

    const renderMark = (newsTheme: BreakingNewsThemeName | undefined) => {
        if (!newsTheme) {
            return <StyledBreakingNewsMark breakingNewsTheme={'default'} />
        }

        const theme = getTheme(newsTheme)

        switch (theme) {
            case 'default':
                return <StyledBreakingNewsMark breakingNewsTheme={'default'} />
            default:
                return <StyledAnnouncementMark breakingNewsTheme={newsTheme} />
        }
    }

    const renderLogo = (newsTheme: BreakingNewsThemeName | undefined) => {
        if (!newsTheme) {
            return <StyledIconChevronRight />
        }

        const cricketPlayerImage = require<string>('./assets/pick11-cricketplayer.png')
        const cricketPlayerImage2x = require<string>('./assets/pick11-cricketplayer@2x.png')

        switch (newsTheme) {
            case 'cricket':
                return (
                    <StyledLogoContainer>
                        <StyledSevenCricketLogo
                            src={require<string>('./assets/7cricketlogo.png')}
                            alt="Seven News Cricket Live and in HD"
                        />
                    </StyledLogoContainer>
                )
            case 'thegame':
                return <StyledTheGameLogo />
            case 'csk':
                return
            case 'pick11':
                return (
                    <StyledCricketPlayerWrapper>
                        <StyledCricketPlayerImage
                            srcSet={`${cricketPlayerImage},
                                ${cricketPlayerImage2x} 2x,`}
                            src={cricketPlayerImage}
                        />
                        <StyledPick11Logo />
                    </StyledCricketPlayerWrapper>
                )
            default:
                return <StyledIconChevronRight />
        }
    }

    return (
        <ImpressionAvailable
            loading={false}
            available={() => {
                onEvent(
                    createCollectionAvailableEvent(
                        [
                            `Breaking News: ${renderTitlePrefix(titlePrefix, breakingNewsTheme)} ${
                                item.shortHeadline
                            }`,
                        ],
                        `Breaking News`,
                        {
                            cardFormat: breakingNewsTheme ?? 'default',
                        }
                    )
                )
            }}
        >
            {ref => (
                // TODO You can't pass a ref down to a custom component. We should be able
                // to fix this with emotion 10. Wrapping in a div for now
                <div ref={ref}>
                    <StyledBreakingNews
                        to={item.link}
                        onClick={linkClicked}
                        breakingNewsTheme={getTheme(breakingNewsTheme)}
                    >
                        <StyledWrapper>
                            {renderMark(breakingNewsTheme)}
                            <StyledBreakingNewsTitle
                                breakingNewsTheme={getTheme(breakingNewsTheme)}
                                fontScale={1}
                            >
                                {`${renderTitlePrefix(titlePrefix, breakingNewsTheme)} ${
                                    item.shortHeadline
                                }`}
                            </StyledBreakingNewsTitle>
                        </StyledWrapper>
                        {renderLogo(breakingNewsTheme)}
                    </StyledBreakingNews>
                </div>
            )}
        </ImpressionAvailable>
    )
}
BreakingNews.displayName = 'BreakingNews'
